import type { AudienceInsightsResponse } from "@nlc/shared-types";
import type { ComponentType } from "react";

import type { SocialIconProps } from "@/features/brands/create/shared/components/utils/socialMedia";

import { Loader } from "@/components/Loader";
import { Facebook } from "@/features/brands/create/shared/components/icons/FacebookIcon";
import { Instagram } from "@/features/brands/create/shared/components/icons/InstagramIcon";
import { useUserTimezone } from "@/hooks/useUserTimezone";

import type { InsightsFilters } from "./insightsFilters";

import { useAudienceInsights } from "../../hooks/useAudienceInsights";
import { formatMetric } from "./formatMetric";
import { Card } from "./overview/Card";

const PLATFORMS: Partial<
  Record<
    AudienceInsightsResponse["platforms"][number]["platform"],
    { Icon: ComponentType<SocialIconProps>; label: string }
  >
> = {
  facebook: { Icon: Facebook, label: "Facebook" },
  instagram: { Icon: Instagram, label: "Instagram" },
};

export function AudienceInsights({ filters }: { filters: InsightsFilters }) {
  const { timezone } = useUserTimezone();
  const { data, isLoading } = useAudienceInsights({
    brandIds: filters.brandIds,
    days: filters.days,
    platform: filters.platform,
    timezone,
  });

  if (isLoading || !data) return <Loader className="py-20" />;

  const { ageGroups, gender, platforms } = data;

  return (
    <div className="flex flex-col gap-2xl p-xl">
      <div className="flex flex-col gap-xs">
        <h2 className="text-display-xs font-semibold text-text-primary">
          Your audience
        </h2>
        <p className="text-sm text-text-tertiary">
          Who follows you, and how that changed in this period.
        </p>
      </div>

      <div className="grid items-start gap-lg lg:grid-cols-[2fr_3fr]">
        <FollowersCard platforms={platforms} />
        <Card title="Who follows you">
          <p className="-mt-md text-sm text-text-tertiary">
            Instagram followers by age and gender
          </p>
          {ageGroups.length > 0 && <AgeBreakdown ageGroups={ageGroups} />}
          {gender && <GenderBreakdown gender={gender} />}
          {ageGroups.length === 0 && !gender && (
            <p className="py-xl text-center text-sm text-text-secondary">
              No age and gender data yet.
            </p>
          )}
          <p className="text-sm text-text-tertiary">
            Needs an Instagram account with at least 100 followers. Facebook
            doesn&apos;t share follower age and gender.
          </p>
        </Card>
      </div>

      <p className="text-sm text-text-tertiary">
        Numbers come from Instagram and Facebook and refresh once a day.
      </p>
    </div>
  );
}

function FollowersCard({
  platforms,
}: {
  platforms: AudienceInsightsResponse["platforms"];
}) {
  const total = platforms.reduce((sum, { followers }) => sum + followers, 0);
  const totalNew = platforms.reduce(
    (sum, { newFollowers }) => sum + newFollowers,
    0,
  );

  return (
    <Card title="Followers">
      {platforms.length === 0 ? (
        <p className="py-xl text-center text-sm text-text-secondary">
          Link a Facebook or Instagram page to see your followers.
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-xs">
            <span className="text-display-md font-semibold text-text-primary">
              {formatMetric(total)}
            </span>
            <span className="text-sm font-medium text-text-success-primary">
              +{formatMetric(totalNew)} new this period
            </span>
          </div>
          <ul className="flex flex-col gap-lg">
            {platforms.map(({ followers, newFollowers, platform }) => {
              const details = PLATFORMS[platform];

              if (!details) return null;

              const { Icon, label } = details;

              return (
                <li
                  className="flex items-center justify-between gap-md rounded-xl bg-bg-secondary p-lg"
                  key={platform}
                >
                  <span className="flex items-center gap-md font-semibold text-text-primary">
                    <Icon size={24} />
                    {label}
                  </span>
                  <span className="flex flex-col items-end">
                    <span className="text-lg font-semibold text-text-primary">
                      {formatMetric(followers)}
                    </span>
                    <span className="text-sm text-text-success-primary">
                      +{formatMetric(newFollowers)} new
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Card>
  );
}

function AgeBreakdown({
  ageGroups,
}: {
  ageGroups: AudienceInsightsResponse["ageGroups"];
}) {
  const maxPercent = Math.max(...ageGroups.map(({ percent }) => percent), 1);

  return (
    <ul className="flex flex-col gap-md">
      {ageGroups.map(({ label, percent }) => (
        <li className="flex items-center gap-lg text-sm" key={label}>
          <span className="w-12 shrink-0 font-medium text-text-secondary">
            {label}
          </span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-bg-tertiary">
            <div
              className="h-full rounded-full bg-fg-brand-primary"
              style={{ width: `${(percent / maxPercent) * 100}%` }}
            />
          </div>
          <span className="w-10 shrink-0 text-right font-semibold text-text-primary">
            {percent}%
          </span>
        </li>
      ))}
    </ul>
  );
}

function GenderBreakdown({
  gender,
}: {
  gender: NonNullable<AudienceInsightsResponse["gender"]>;
}) {
  const groups = [
    { label: "Women", opacity: 1, percent: gender.women },
    { label: "Men", opacity: 0.6, percent: gender.men },
  ];

  return (
    <div className="flex flex-col gap-md">
      <div className="flex h-2 gap-xxs overflow-hidden rounded-full">
        {groups.map(({ label, opacity, percent }) => (
          <div
            className="h-full bg-fg-brand-primary"
            key={label}
            style={{ opacity, width: `${percent}%` }}
          />
        ))}
      </div>
      <div className="flex items-center justify-between gap-md text-sm">
        {groups.map(({ label, opacity, percent }) => (
          <span className="flex items-center gap-sm" key={label}>
            <span
              className="size-2 rounded-xs bg-fg-brand-primary"
              style={{ opacity }}
            />
            <span className="font-medium text-text-secondary">{label}</span>
            <span className="font-semibold text-text-primary">{percent}%</span>
          </span>
        ))}
      </div>
    </div>
  );
}
